import { db } from "../firebaseClient"
import { collection, doc, getDoc, getDocs, query, setDoc, where } from "firebase/firestore"

const SUFFIX = ".nesol"

export const normalizeUsername = (input) => (input || "").trim().toLowerCase().replace(/\.nesol$/, "")

// 3-20 chars, must start with a letter, lowercase letters/numbers/underscore/hyphen only
export const isValidUsername = (username) => /^[a-z][a-z0-9_-]{2,19}$/.test(username || "")

export const formatNesolearnId = (username) => `${normalizeUsername(username)}${SUFFIX}`

// Checks whether a user already has a Nesolearn ID claimed. Deliberately
// queries the public nesolearnIds collection by uid rather than reading
// users/{uid} directly — the users collection is locked to "only the owner
// (or a platform admin) can read their own doc," so a second party (e.g.
// an org owner checking someone else before designating them a mentor)
// would always get a silent permission-denied there and see "no ID" even
// when one exists. nesolearnIds is public-read by design, so this works
// for anyone asking, about anyone.
export const getNesolearnId = async (uid) => {
  if (!uid) return { nesolearnId: null, error: null }
  try {
    const q = query(collection(db, "nesolearnIds"), where("uid", "==", uid))
    const snap = await getDocs(q)
    if (snap.empty) return { nesolearnId: null, error: null }
    return { nesolearnId: snap.docs[0].id, error: null }
  } catch (error) {
    return { nesolearnId: null, error }
  }
}

// Checks if a desired username is available, without claiming it
export const checkNesolearnIdAvailable = async (username) => {
  const clean = normalizeUsername(username)

  if (!isValidUsername(clean)) {
    return {
      available: false,
      id: null,
      error: { message: "Use 3-20 characters, starting with a letter — letters, numbers, _ or - only." },
    }
  }

  try {
    const id = formatNesolearnId(clean)
    const snap = await getDoc(doc(db, "nesolearnIds", id))
    return { available: !snap.exists(), id, error: null }
  } catch (error) {
    return { available: false, id: null, error }
  }
}

// Claims a username as this user's Nesolearn ID. Re-checks availability at
// claim time (not just relying on an earlier check) to close the race
// between checking and claiming. Idempotent: if the user already has an ID,
// returns that instead of claiming a new one.
// `extra` lets callers tag the lookup doc — organizations pass
// { accountType: "organization", orgId } so the shared ID namespace can
// tell individuals and orgs apart without needing a separate collection.
export const claimNesolearnId = async (uid, email, username, extra = {}) => {
  if (!uid) return { nesolearnId: null, error: { message: "Missing uid" } }

  const { nesolearnId: existing } = await getNesolearnId(uid)
  if (existing) return { nesolearnId: existing, error: null }

  const clean = normalizeUsername(username)
  if (!isValidUsername(clean)) {
    return {
      nesolearnId: null,
      error: { message: "Use 3-20 characters, starting with a letter — letters, numbers, _ or - only." },
    }
  }

  try {
    const id = formatNesolearnId(clean)
    const lookupRef = doc(db, "nesolearnIds", id)
    const lookupSnap = await getDoc(lookupRef)

    if (lookupSnap.exists()) {
      return { nesolearnId: null, error: { message: "That ID is already taken. Try another." } }
    }

    await setDoc(lookupRef, { id, uid, email: email || "", accountType: "individual", ...extra })
    await setDoc(doc(db, "users", uid), { nesolearnId: id }, { merge: true })

    return { nesolearnId: id, error: null }
  } catch (error) {
    return { nesolearnId: null, error }
  }
}

// Resolves a Nesolearn ID to its owner's email — needed pre-auth since
// Firebase's client SDK only signs in with email/password.
// NOTE: publicly readable by design so this works before login (see rules).
// Known, accepted tradeoff at pilot scale — a real fix means server-side
// resolution via Cloud Functions later.
export const resolveNesolearnIdToEmail = async (nesolearnId) => {
  try {
    const snap = await getDoc(doc(db, "nesolearnIds", nesolearnId))
    if (!snap.exists()) return { email: null, error: null }
    return { email: snap.data()?.email || null, error: null }
  } catch (error) {
    return { email: null, error }
  }
}

// Login identifier could be an email, a full "name.nesol" ID, an old-style
// "N1234" ID, or just the bare username with the suffix omitted — normalize
// whatever was typed into a lookup-ready ID.
export const identifierToLookupId = (identifier) => {
  const trimmed = (identifier || "").trim()
  if (trimmed.includes("@")) return null // it's an email, not an ID
  if (/^n\d{4}$/i.test(trimmed)) return trimmed.toUpperCase() // legacy numeric ID
  return trimmed.toLowerCase().endsWith(SUFFIX) ? trimmed.toLowerCase() : `${trimmed.toLowerCase()}${SUFFIX}`
}